import "../step.js";
import "#/shared/api/api-host-global.js";
import "../summary.js";
import "../mixins/appointment_slot_picker.js";
import "./confirmation/modals/recoverable_error_modal.js";
import "./confirmation/callouts/edit_appointment_callout.js";
import "./confirmation/callouts/invalid_patient_type_callout.js";
import "./confirmation/callouts/provider_switch_callout.js";
import "./confirmation/fields/platform_select_field.js";
import "../field.js";

window.AppointmentBooking ??= {};
window.AppointmentBooking.Wizard ??= {};
window.AppointmentBooking.Wizard.Views ??= {};

const __ConfirmationStepPrior = AppointmentBooking.Wizard.Views.ConfirmationStep;
AppointmentBooking.Wizard.Views.ConfirmationStep = class ConfirmationStep extends (
  AppointmentBooking.Wizard.Views.Mixins.AppointmentSlotPicker(AppointmentBooking.Wizard.Views.Step)
) {
  constructor() {
    super(...arguments);
    this.reset = this.reset.bind(this);
    this.recoverableErrorTypes = this.recoverableErrorTypes.bind(this);
    this.validatePrerequisites = this.validatePrerequisites.bind(this);
    this.areRequirementsValid = this.areRequirementsValid.bind(this);
    this.initialize = this.initialize.bind(this);
    this.handleEditAppointmentCalloutToggle = this.handleEditAppointmentCalloutToggle.bind(this);
    this.updateSuggesterIfConditionsChanged = this.updateSuggesterIfConditionsChanged.bind(this);
    this.render = this.render.bind(this);
    this.renderSuggester = this.renderSuggester.bind(this);
    this.whenSlotSuggesterLoaded = this.whenSlotSuggesterLoaded.bind(this);
    this.initializeSlotSuggester = this.initializeSlotSuggester.bind(this);
    this.getPageTitle = this.getPageTitle.bind(this);
    this.updateValidatedEHRAppointmentSlotId = this.updateValidatedEHRAppointmentSlotId.bind(this);
    this.selectSlot = this.selectSlot.bind(this);
    this.hasInvalidPatientType = this.hasInvalidPatientType.bind(this);
    this.providerWasSwitched = this.providerWasSwitched.bind(this);
    this.userHasSelectedSlot = this.userHasSelectedSlot.bind(this);
    this.shouldShowSlotSuggester = this.shouldShowSlotSuggester.bind(this);
    this.validateComplete = this.validateComplete.bind(this);
    this.encounterHistoryRestriction = this.encounterHistoryRestriction.bind(this);
    this.slotSelected = this.slotSelected.bind(this);
  }

  reset() {
    this.model.resetErrors(); // Don't allow errors to persist before this step
    this.editAppointmentCallout.resetShownByUser(); // Forget that we've shown the edit appointment callout
    this.model.set({ validated_user_selected_slot: false });
    return this.whenSlotSuggesterLoaded((suggester) => suggester.setSelectedSlotId(null));
  }

  recoverableErrorTypes() {
    return this.recoverableErrorModal
      .getRecoverableErrorTypes()
      .concat(this.providerSwitchCallout.getRecoverableErrorTypes())
      .concat(this.invalidPatientTypeCallout.getRecoverableErrorTypes())
      .concat(this.editAppointmentCallout.getRecoverableErrorTypes());
  }

  validatePrerequisites() {
    return Sparkle.Utilities.OrderedAJAX.post(
      "AppointmentBookingWizard:validate",
      this.appointmentValidationUrl,
      this.model.toValidationJSON(),
    ).then((json) => this.model.setWithPrefix(json, "validated_", { except: ["errors", "error_messages"] }));
  }

  areRequirementsValid() {
    return this.isAttributeInputValid("comments") && !!this.model.get("validated_ehr_appointment_slot_id");
  }

  initialize() {
    super.initialize(...arguments);

    this.recoverableErrorModal = new AppointmentBooking.Wizard.Views.ConfirmationStep.RecoverableErrorModal({
      model: this.model,
      condition: () => this.recoverableErrorModal.hasRecoverableErrors(),
    });

    this.summary = new AppointmentBooking.Wizard.Views.Summary({ model: this.model });

    this.providerSwitchCallout = new AppointmentBooking.Wizard.Views.ConfirmationStep.ProviderSwitchCallout({
      model: this.model,
      anchor: "top-left",
      condition: () =>
        !this.editAppointmentCallout.isVisible() && this.providerWasSwitched() && !this.userHasSelectedSlot(),
      analytics: "message-shown",
    });

    this.invalidPatientTypeCallout = new AppointmentBooking.Wizard.Views.ConfirmationStep.InvalidPatientTypeCallout({
      model: this.model,
      condition: () =>
        this.hasInvalidPatientType() && !this.userHasSelectedSlot() && !this.editAppointmentCallout.isVisible(),
      analytics: "message-shown",
    });

    this.editAppointmentCallout = new AppointmentBooking.Wizard.Views.ConfirmationStep.EditAppointmentCallout({
      parent: this,
      model: this.model,
      anchor: "top-right",
      condition: () =>
        this.editAppointmentCallout.isShownByUser() ||
        (!this.providerWasSwitched() &&
          !this.hasInvalidPatientType() &&
          !this.model.get("validated_ehr_appointment_slot_id")) ||
        this.editAppointmentCallout.hasRecoverableErrors(),

      analytics: "edit-appointment-callout-shown",
    });

    this.insuranceField = new AppointmentBooking.Wizard.Views.Field({
      template: "step/confirmation/fields/insurance",
      model: this.model,
      condition: () => this.model.hasInsuranceToReview(),
    });

    this.reasonForVisitField = new AppointmentBooking.Wizard.Views.Field({
      template: "step/confirmation/fields/reason_for_visit",
      model: this.model,
      condition: () => !!this.model.get("validated_ehr_appointment_slot_id"),
    });

    this.platformSelectField = new AppointmentBooking.Wizard.Views.ConfirmationStep.PlatformSelectField({
      model: this.model,
      condition: () => !!this.model.get("validated_ehr_appointment_slot_id"),
    });

    this.listenTo(this.model, "change:validated_valid", this.render);
    this.listenTo(this.model, "change:validated_visit_type_id", this.updateValidatedEHRAppointmentSlotId);

    this.listenTo(this.editAppointmentCallout, "show", this.handleEditAppointmentCalloutToggle);

    this.listenTo(this.editAppointmentCallout, "hide", this.handleEditAppointmentCalloutToggle);

    this.listenToIfRenderable(this.model, "change", this.updateSuggesterIfConditionsChanged.bind(this)); // Update the suggester once on change instead of adding listeners for each attribute's change event
    return this.whenSlotSuggesterLoaded((suggester) =>
      this.listenToIfEnabled(suggester.model, "change:selectedSlotId", (_, value) => this.selectSlot(value)),
    ); // Update the wizard with the selected suggested slot
  }

  // EVENT HANDLERS

  handleEditAppointmentCalloutToggle() {
    this.providerSwitchCallout.updateVisibility();
    return this.invalidPatientTypeCallout.updateVisibility();
  }

  updateSuggesterIfConditionsChanged(model) {
    if (
      model.attributesChanged(
        "validated_ehr_appointment_slot_id",
        "validated_appointment_slot",
        "validated_visit_type_id",
      )
    ) {
      return this.renderSuggester();
    }
  }

  // RENDERING

  render() {
    if (!this.renderable) {
      return this;
    }

    super.render(...arguments);

    this.replaceContents(".recoverable-error-modal-container", this.recoverableErrorModal.render().el);
    this.replaceContents(".summary-container", this.summary.render().el);
    this.replaceContents(
      ".provider-switch-callout-container",
      this.providerSwitchCallout.render().el,
      this.invalidPatientTypeCallout.render().el,
      this.editAppointmentCallout.render().el,
    );
    this.replaceContents(".platform-select-container", this.platformSelectField.render().el);
    this.replaceContents(".insurance-container", this.insuranceField.render().el);
    this.replaceContents(".reason-for-visit-container", this.reasonForVisitField.render().el);
    this.renderSuggester();

    return this;
  }

  renderSuggester() {
    if (this.loadingSuggester) {
      return;
    }

    this.loadingSuggester = true;

    return this.whenSlotSuggesterLoaded((suggester) => {
      this.loadingSuggester = false;
      this.clearContents(".slot-reselector-container");

      if (!this.shouldShowSlotSuggester()) {
        return;
      }

      this.replaceContents(".slot-reselector-container", suggester.el);

      // Updates the suggester with the slot conditions currently selected in the booking
      return suggester.setDesiredSlotConditions({
        id: this.model.get("appointment_slot")?.id,
        // Override the conditions inherited from the desired slot with the user's updated choices
        visitTypeId: this.model.get("validated_visit_type_id") || this.model.get("visit_type_id"),
        patientType: this.model.get("validated_returning_patient") ? "returning" : "new",
        resourceId: this.model.get("validated_provider")?.id,
        resourceType: this.model.get("validated_provider")?.type,
        startTime: this.model.get("appointment_slot")?.start_time,
        providerGroupIds: this.model.get("switch_provider_group_ids"),
      });
    });
  }

  // HELPERS

  whenSlotSuggesterLoaded(fn) {
    return this.initializeSlotSuggester().then(fn);
  }

  initializeSlotSuggester() {
    return (
      this.slotSuggesterPromise ||
      (this.slotSuggesterPromise = AppointmentBooking.initializeSlotSuggester({
        variant: "confirmation",
        showMessage: false,
      }))
    );
  }

  getPageTitle() {
    return `${this.I18n().confirmation.page_title} - ${this.model.get("appointment_slot").visit_type_name} - ${this.I18n().login_type[this.model.get("login_type")]}`;
  }

  updateValidatedEHRAppointmentSlotId() {
    return this.model.set(
      "validated_ehr_appointment_slot_id",
      this.model.get("validated_visit_type")?.matching_slot?.id,
    );
  }

  selectSlot(slotId) {
    this.model.set({ validated_ehr_appointment_slot_id: slotId, validated_user_selected_slot: true });
    this.model.resetErrors(); // The user has picked a new slot so clear out the error messages
    return this.scrollTo(this.el);
  }

  toggleEditAppointmentCallout() {
    return this.editAppointmentCallout.toggleByUser();
  }

  hasInvalidPatientType() {
    return this.invalidPatientTypeCallout.hasRecoverableErrors();
  }

  providerWasSwitched() {
    return (
      this.model.get("validated_provider")?.id &&
      this.model.get("provider").id !== this.model.get("validated_provider").id
    );
  }

  userHasSelectedSlot() {
    return this.model.get("validated_user_selected_slot");
  }

  shouldShowSlotSuggester() {
    return this.validateComplete() && !this.encounterHistoryRestriction() && !this.slotSelected();
  }

  validateComplete() {
    return this.model.get("validated_valid") != null;
  }

  encounterHistoryRestriction() {
    return this.model.get("validated_invalid_patient_type_message_key") === "no_slots";
  }

  slotSelected() {
    return !!this.model.get("validated_ehr_appointment_slot_id");
  }
};
AppointmentBooking.Wizard.Views.ConfirmationStep.prototype.template = "step/confirmation";
AppointmentBooking.Wizard.Views.ConfirmationStep.prototype.className = "appointment-booking-wizard-confirmation-step";
AppointmentBooking.Wizard.Views.ConfirmationStep.prototype.name = "Review";

AppointmentBooking.Wizard.Views.ConfirmationStep.prototype.appointmentValidationUrl = window.Sparkle.apiHost
  ? window.Sparkle.apiHost + window.SparkleConfig.routes.embed.validateEhrAppointments
  : undefined;

AppointmentBooking.Wizard.Views.ConfirmationStep.prototype.events = {
  'click [data-appointment-booking-wizard-behaviour="edit-appointment-slot"]': "toggleEditAppointmentCallout",
};

if (__ConfirmationStepPrior) {
  Object.assign(AppointmentBooking.Wizard.Views.ConfirmationStep, __ConfirmationStepPrior);
}
